import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import {
  KpiDetailRow,
  KpiModel,
  RawKpi,
  kpiDetailRows,
  orderAndFilterKpis
} from '@core/models/kpi.model';
import { selectPlanChanges } from '@core/store/plan-edit/plan-edit-session.selectors';
import { selectPlan } from '@core/store/plan/plan.selectors';
import {
  selectKpiDecimals,
  selectKpiOrder,
  selectPlanPanelKpiHidden
} from '@core/store/user-preferences/user-preferences.selectors';

import { KpiTone } from '@shared/components/kpi-comparison/kpi-comparison.model';
import { KpiDetailPopupService } from '@shared/services/kpi-detail-popup/kpi-detail-popup.service';

import { PlanPanelService } from '../../plan-panel.service';
import { ImpactKpiTile, impactKpiTiles } from './impact-kpis';
import { sameKpisView, selectPlanKpisInView } from './plan-kpis-in-view';

interface TileView extends ImpactKpiTile {
  prefix: string;
  suffix: string;
  /** Empty for a scalar, which has nothing beyond its value for a card to show. */
  detail: KpiDetailRow[];
}

const TONE_CLASSES: Record<KpiTone, string> = {
  good: 'bg-success-soft text-success-hover',
  bad: 'bg-danger-soft text-danger',
  flat: 'bg-interactive text-text-secondary'
};

const NO_KPIS: RawKpi[] = [];

/** The plan's chosen KPIs, measured against the saved plan or the last change while anyone edits it. */
@Component({
  selector: 'app-kpis-section',
  imports: [TranslatePipe],
  templateUrl: './kpis-section.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: ``
})
export class KpisSectionComponent {
  private readonly store = inject(Store);
  private readonly panel = inject(PlanPanelService);
  private readonly kpiDetailPopup = inject(KpiDetailPopupService);
  protected readonly TONE_CLASSES = TONE_CLASSES;

  private readonly plan = this.store.selectSignal(selectPlan);
  private readonly kpisView = this.store.selectSignal(selectPlanKpisInView, {
    equal: sameKpisView
  });
  protected readonly changes = this.store.selectSignal(selectPlanChanges);
  private readonly kpiOrder = this.store.selectSignal(selectKpiOrder);
  private readonly kpiHidden = this.store.selectSignal(selectPlanPanelKpiHidden);
  private readonly kpiDecimals = this.store.selectSignal(selectKpiDecimals);

  protected readonly mode = computed(() => this.kpisView().mode);
  protected readonly kpisUnavailable = computed(
    () => this.mode() === 'stored' || this.mode() === 'saved-plan'
  );
  /** Comparing the saved plan's KPIs, or provisional ones, against anything would invent a change. */
  protected readonly showDeltas = computed(
    () => !!this.changes()?.settled && this.mode() === 'live'
  );
  protected readonly muted = computed(() =>
    this.changes()
      ? !this.showDeltas()
      : this.mode() === 'saved-plan' || this.mode() === 'provisional'
  );

  private readonly lastRound = computed(() => this.changes()?.lastRound ?? null);
  protected readonly hasRound = this.panel.kpiHasRound;
  protected readonly reference = this.panel.effectiveKpiReference;

  protected readonly tiles = computed<TileView[]>(() => {
    const decimals = this.kpiDecimals();
    const models = (kpis: RawKpi[]) => kpis.map((kpi) => new KpiModel(kpi, decimals));
    const current = orderAndFilterKpis(
      models(this.kpisView().kpis),
      this.kpiOrder(),
      this.kpiHidden()
    );
    const before = this.lastRound()?.before.planKpis;
    return impactKpiTiles({
      current,
      saved: models(this.plan()?.kpis ?? NO_KPIS),
      before: before ? models(before) : null,
      reference: this.reference(),
      showDeltas: this.showDeltas(),
      decimals
    }).map((tile, index) => ({
      ...tile,
      prefix: current[index].metadata.prefix ?? '',
      suffix: current[index].metadata.suffix ?? '',
      detail: kpiDetailRows(current[index])
    }));
  });

  protected affixed(tile: TileView, value: string | null): string {
    return value === null ? '—' : `${tile.prefix}${value}${tile.suffix}`;
  }

  protected toggleDetail(tile: TileView, anchor: HTMLElement): void {
    if (tile.detail.length === 0) return;
    this.kpiDetailPopup.toggle(anchor, {
      labelKey: tile.i18nKey,
      rows: tile.detail,
      placement: 'right-start'
    });
  }

  protected onTileKeydown(tile: TileView, event: Event): void {
    event.preventDefault();
    this.toggleDetail(tile, event.currentTarget as HTMLElement);
  }
}
